/**
 * The tunable numbers of the meta game, named after the GDD (§14 ranked, §15 progression, §11 daily
 * heist). Values marked [T] there are provisional: they are tuned with real matches, and any change
 * goes in the GDD tuning log (§17). The rules of a heist are not here: they are in the engine.
 *
 * Time is in milliseconds since the epoch, amounts of loot in millions of lire, XP in whole points.
 */

import type { Difficulty } from '@il-colpo/engine';

// ---- Experience (GDD §15) -------------------------------------------------------------------

export const XP = {
  /** For playing a heist to the end, whatever happened. */
  participation: 50,
  /** The heist was a success. */
  success: 100,
  /** The player won (GDD §7.1). */
  win: 75,
  /** The player's secret accusation named the real infiltrator. */
  unmaskRight: 100,
  /** The infiltrator won. */
  infiltratorWin: 150,
  /** The first heist played each (UTC) day counts this many times [D, GDD appendix A]. */
  firstOfDayMultiplier: 2,
} as const;

/** Level `n` is completed with `baseXp + perLevelXp · n` points of XP (GDD §15). */
export const LEVELS = {
  baseXp: 200,
  perLevelXp: 50,
  /** A ceiling for the loops and the screens: nobody gets near it. */
  maxLevel: 200,
} as const;

// ---- Ratings, leagues and seasons (GDD §14) --------------------------------------------------

export const RATING = {
  /** OpenSkill's own defaults (the library's `rating()`): 25 and 25/3. */
  initialMu: 25,
  initialSigma: 25 / 3,
  /** The number a player sees: `displayBase + displayScale · ordinal`, so a new player shows 1000. */
  displayBase: 1000,
  displayScale: 40,
  /** The first games of a season only place the player: the league is not shown before (GDD §14). */
  placementGames: 5,
  /**
   * The "house" a table of thieves plays against when nobody is the infiltrator (GDD §14, [D]):
   * a fixed rating that depends on the difficulty. Provisional (risk R7 in the plan).
   */
  houseMu: { easy: 20, normal: 25, pro: 28, legend: 31 } satisfies Record<Difficulty, number>,
  houseSigma: 4,
} as const;

/** Leagues by displayed rating of the thief rating, lowest first (GDD §14) [T]. */
export const LEAGUES = [
  { id: 'pickpocket', minRating: 0 },
  { id: 'thief', minRating: 1000 },
  { id: 'professional', minRating: 1200 },
  { id: 'master', minRating: 1400 },
  { id: 'legend', minRating: 1600 },
] as const;
export type LeagueId = (typeof LEAGUES)[number]['id'];

export const SEASON = {
  /** Season 1 begins on this Monday, 00:00 UTC (2026-10-19). Earlier times count as season 1. */
  anchorMs: 1_792_368_000_000,
  weeks: 8,
  /**
   * What the rating keeps of the distance from the start at a new season [T, GDD §14: "reset
   * parziale"]: the new mu is `initialMu + keep · (mu − initialMu)`, and the sigma grows back.
   */
  keepPermille: 500,
  sigmaFloor: 6,
} as const;

/** The queue ban for those who leave a ranked heist, in minutes, by how many times in a row (GDD §14). */
export const ABANDON = {
  banMinutes: [5, 15, 60],
  /** The count of abandonments starts again after this long without one [T]. */
  forgetAfterMs: 24 * 60 * 60 * 1000,
} as const;

// ---- Daily heist (GDD §11) -------------------------------------------------------------------

export const DAILY = {
  attempts: 3,
  difficulty: 'pro' as Difficulty,
  bots: 3,
} as const;

// ---- Challenges and trophies ------------------------------------------------------------------

export const CHALLENGES = {
  /** How many of each are on offer at a time. */
  dailyCount: 3,
  weeklyCount: 3,
} as const;
